import { FINANCE_NOT_ENROLLED_EVENT, adminApi } from '@/api/client';
import {
  EMPTY_IDENTITY,
  type FinanceIdentity,
  can as canPermission,
  parseFinanceIdentity,
} from '@/layouts/permissions';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface PermissionsState {
  /** `/ext/finance/me` 的完整身份（含员工档案与岗位）。 */
  identity: FinanceIdentity;
  /** 权限切面，等价于 identity（wildcard + permissions）。 */
  grant: FinanceIdentity;
  /** 是否还在拉身份。未加载完不能放行任何页面（fail closed）。 */
  loading: boolean;
  /** 身份接口本身挂了（不是「没权限」）。 */
  failed: boolean;
  can: (permission: string) => boolean;
  /** 自助改档后刷新身份（档案会显示在顶栏 / 我的档案页）。 */
  reload: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsState | null>(null);

/**
 * 从 `GET /admin-api/ext/finance/me` 取当前登录人的财务身份并向下提供。
 *
 * 后端按员工档案的岗位派生权限位；未建档 / 已离职的后台账号 `can_login=false`，
 * 由 FinanceLayout 整站阻断。菜单仍在 layouts/permissions.ts 里按财务自己的 IA 生成。
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<FinanceIdentity>(EMPTY_IDENTITY);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (alive: () => boolean) => {
    try {
      const response = await adminApi.get<unknown>('/ext/finance/me');
      if (!alive()) return;
      const parsed = parseFinanceIdentity(response.data);
      if (!parsed) throw new Error('财务身份接口返回格式不兼容');
      setIdentity(parsed);
      setFailed(false);
    } catch {
      if (!alive()) return;
      setIdentity(EMPTY_IDENTITY);
      setFailed(true);
    } finally {
      if (alive()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    void load(() => alive);
    return () => {
      alive = false;
    };
  }, [load]);

  // 会话中途被移出财务系统（离职 / 删档）：axios 层广播 finance_not_enrolled 后
  // 立刻重拉身份，can_login 变 false 就由 FinanceLayout 整站阻断。
  useEffect(() => {
    let alive = true;
    const onNotEnrolled = () => {
      void load(() => alive);
    };
    window.addEventListener(FINANCE_NOT_ENROLLED_EVENT, onNotEnrolled);
    return () => {
      alive = false;
      window.removeEventListener(FINANCE_NOT_ENROLLED_EVENT, onNotEnrolled);
    };
  }, [load]);

  const value = useMemo<PermissionsState>(
    () => ({
      identity,
      grant: identity,
      loading,
      failed,
      can: (permission: string) => canPermission(identity, permission),
      reload: () => load(() => true),
    }),
    [identity, loading, failed, load],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions(): PermissionsState {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within <PermissionsProvider>');
  return ctx;
}
